
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ProductGallery from './components/ProductGallery';
import ProductInfo from './components/ProductInfo';
import ProductTabs from './components/ProductTabs';
import RelatedProducts from './components/RelatedProducts';

export default function ProductDetailPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    // Ensure the page starts at the top when this component mounts
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    // Clean‑up listener on unmount to avoid memory leaks
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white border-b border-gray-100'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <Link to="/" className="cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/7d05fb9254e2e5292eff42edf988a3d0/fec3bdf8c556789036dd9c4db0ff716f.png"
                  alt="Company Logo"
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/"
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                홈
              </Link>
              <Link
                to="/#products"
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                제품 카테고리
              </Link>
              <Link
                to="/#features"
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                주요 특징
              </Link>
              <Link
                to="/#partners"
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                파트너
              </Link>
              <Link
                to="/#contact"
                className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                문의하기
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-teal-600 transition-colors cursor-pointer">
              홈
            </Link>
            <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
            <Link to="/#products" className="hover:text-teal-600 transition-colors cursor-pointer">
              제품 카테고리
            </Link>
            <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
            <span className="text-gray-400">SSD 저장장치</span>
            <i className="ri-arrow-right-s-line mx-2 text-gray-400"></i>
            <span className="text-gray-900 font-medium">삼성전자 990 PRO 2TB</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <ProductGallery />
          <ProductInfo />
        </div>

        {/* Tabs */}
        <ProductTabs />

        {/* Inquiry Section */}
        <section
          id="product-inquiry"
          className="mt-16 bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">대량 구매 및 견적 문의</h3>
              <p className="text-teal-100 mb-6 leading-relaxed">
                기업 및 단체 대량 구매 시 특별 할인가를 제공합니다.
                맞춤 견적이 필요하시면 언제든 문의해 주세요.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-lg"></i>
                  <span className="text-sm">10개 이상 대량 구매 할인</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-lg"></i>
                  <span className="text-sm">기업 전용 세금계산서 발행</span>
                </li>
                <li className="flex items-center gap-3">
                  <i className="ri-check-line text-lg"></i>
                  <span className="text-sm">전담 매니저 배정</span>
                </li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#contact"
                  className="bg-white text-teal-700 px-6 py-3 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  견적 문의하기
                </Link>
                <a
                  href="tel:027020999"
                  className="border-2 border-white text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-white/10 transition-colors text-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>02-702-0999
                </a>
              </div>
            </div>
            <div className="w-full h-64 rounded-xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/be92d140570c4cd8734e2873a7a844f3.jpg"
                alt="대량 구매 문의"
                className="w-full h-full object-cover object-top rounded-xl"
              />
            </div>
          </div>
        </section>

        {/* Related Products */}
        <RelatedProducts />
      </main>

      {/* Footer */}
      <footer className="bg-teal-600 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://static.readdy.ai/image/7d05fb9254e2e5292eff42edf988a3d0/fec3bdf8c556789036dd9c4db0ff716f.png"
                alt="Company Logo"
                className="h-10 w-auto mb-4"
              />
              <p className="text-sm text-teal-100 mb-2">(주)에코브이</p>
              <p className="text-sm text-teal-100">컴퓨터 주변기기 및 정보보안 전문 기업</p>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4">제품</h3>
              <ul className="space-y-2 text-sm text-teal-100">
                <li>
                  <Link to="/#products" className="hover:text-white transition-colors cursor-pointer">
                    RAM 메모리
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-white transition-colors cursor-pointer">
                    CPU 프로세서
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-white transition-colors cursor-pointer">
                    SSD 저장장치
                  </Link>
                </li>
                <li>
                  <Link to="/#products" className="hover:text-white transition-colors cursor-pointer">
                    정보보안필름
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4">회사</h3>
              <ul className="space-y-2 text-sm text-teal-100">
                <li>
                  <Link to="/#partners" className="hover:text-white transition-colors cursor-pointer">
                    파트너
                  </Link>
                </li>
                <li>
                  <Link to="/#news" className="hover:text-white transition-colors cursor-pointer">
                    기술 뉴스
                  </Link>
                </li>
                <li>
                  <Link to="/#contact" className="hover:text-white transition-colors cursor-pointer">
                    문의하기
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-base font-semibold mb-4">연락처</h3>
              <ul className="space-y-2 text-sm text-teal-100">
                <li className="flex items-start">
                  <i className="ri-phone-line mr-2 mt-0.5"></i>
                  <span>02 702 0999</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-mail-line mr-2 mt-0.5"></i>
                  <div>
                    <div>ecov2025@naver.com</div>
                    <div>www.eco-v.co.kr</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-line mr-2 mt-0.5"></i>
                  <div>
                    <div>서울시 용산구 새창로 181</div>
                    <div>선인상가 21동 4층 4086호</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <i className="ri-building-line mr-2 mt-0.5"></i>
                  <div>
                    <div>대표: 박종석</div>
                    <div>사업자등록번호: 110111-0921242</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-teal-500 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-teal-100">
            <p>&copy; 2025 (주)에코브이. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-white transition-colors mt-4 md:mt-0"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
